import {
	createEntityAdapter,
	createSlice,
	createAsyncThunk,
	current,
} from '@reduxjs/toolkit'
import { envAwarePost } from '../../util/util'

export const applyRecipeUpdatesToMeals = createAsyncThunk(
	'recipes/applyUpdatesToMeals',
	async payload => envAwarePost(
		'api/recipes/',
		{
			action:'applyRecipeUpdatesToMeals',
			...payload
		}
	)
)

export const editRecipeMacro = createAsyncThunk(
	'recipes/editMacro',
	async payload => envAwarePost(
		'api/recipes/',
		{
			action:'editRecipeMacro',
			...payload
		}
	)
)

export const removeRecipe = createAsyncThunk(
	'recipes/remove',
	async payload => envAwarePost(
		'api/recipes/',
		{
			action:'removeRecipe',
			...payload
		}
	)
)

export const swapFood = createAsyncThunk(
	'recipes/swapFood',
	async payload => envAwarePost('api/recipes/',{
		action:'editRecipeFood',
		...payload
	})
)

export const reorderRecipeFoods = createAsyncThunk(
	'recipes/reorderFoods',
	async payload => envAwarePost('api/recipes',{
		action:'reorderRecipeFoods',
		...payload
	})
)

export const removeRecipeFood = createAsyncThunk(
	'recipes/removeFood',
	async payload => envAwarePost('api/recipes/',{
		action:'removeRecipeFood',
		...payload
	})
)

export const editRecipeFood = createAsyncThunk(
	'recipes/editFood',
	async payload => envAwarePost('api/recipes/',{
		action:'editRecipeFood',
		...payload
	})
)

export const addFoodToRecipe = createAsyncThunk(
	'recipes/addFood',
	async (payload,{getState}) => {
		const state = getState()
		const recipe_position = state.recipes.entities[payload.recipe_id].foods.length
		return envAwarePost('api/recipes/',{
			action:'addFoodToRecipe',
			recipe_position,
			...payload
		})
	}
)

export const fetchByTag = createAsyncThunk(
	'recipes/fetchByTag',
	async payload => envAwarePost('api/recipes/', {
		action:'readRecipes',
		use_standardized_response:1,
		...payload
	})
)

export const readTags = createAsyncThunk(
	'recipes/readTags',
	async payload => envAwarePost('api/recipes/', {
		action:'readTags',
		...payload
	})
)

export const createTag = createAsyncThunk(
	'recipes/createTag',
	async payload => envAwarePost('api/recipes/', {
		action:'createTag',
		...payload
	})
)

export const deleteTag = createAsyncThunk(
	'recipes/deleteTag',
	async payload => envAwarePost('api/recipes/', {
		action:'deleteTag',
		...payload
	})
)

export const createRecipe = createAsyncThunk(
	'recipes/createRecipe',
	async payload => envAwarePost('api/recipes/', {
		action:'createRecipe',
		...payload
	})
)

export const addTagToRecipe = createAsyncThunk(
	'recipes/addTagToRecipe',
	async payload => envAwarePost('api/recipes/', {
		action:'addTagToRecipe',
		...payload
	})
)

export const addMultipleTagsToRecipe = createAsyncThunk(
	'recipes/addMultipleTagsToRecipe',
	async payload => envAwarePost('api/recipes/', {
		action:'addMultipleTagsToRecipe',
		...payload
	})
)

export const editRecipe = createAsyncThunk(
	'recipes/editRecipe',
	async payload => envAwarePost('api/recipes/', {
		action:'editRecipe',
		...payload
	})
)

export const deleteDefaultRecipe = createAsyncThunk(
	'recipes/deleteDefaultRecipe',
	async payload => envAwarePost('api/recipes/', {
		action:'deleteDefaultRecipe',
		...payload
	})
)

export const addRecipeMacros = createAsyncThunk(
	'recipes/addRecipeMacros',
	async payload => envAwarePost('api/recipes/', {
		action:'addRecipeMacros',
		//previous implementation of this api call
		//would wipe all macros then add new ones so 
		//to add one you had to send all previous w/
		//the one new one.... so added a flag to make 
		//this api call "sane"
		keep_originals:true,
		...payload
	})
)

export const removeMacro = createAsyncThunk(
	'recipes/removeMacro',
	async payload => envAwarePost('api/recipes/', {
		action:'removeMacro',
		...payload
	})
)

const adapter = createEntityAdapter()

const recipesSlice = createSlice({
	name: 'recipes',
	initialState: adapter.getInitialState({ 
		status: 'init', 
		tags: [],
		owners:[],
		copy: {
			show:false,
			id:null
		},
		viewer: {
			show: false,
			id:null
		},
		editor: {
			show:false,
			id:null
		}
	}),
	reducers: {
		setCopy:(state,action)=>{
			state.copy.show = action.payload.show
			state.copy.id = action.payload.id
		},
		updateViewer:(state,action)=>{
			state.viewer = {
				...state.viewer,
				...action.payload
			}
		}
	},
	extraReducers: {
		[fetchByTag.fulfilled]: (state, action) => {
			const { success, data } = action.payload
			if (success == 1) {
				adapter.upsertMany(state,data.recipes)
				state.owners = data.owners
				state.status = 'idle'
			}
		},

		[fetchByTag.pending]: (state, action) => {
			if(Boolean(action.meta.arg.clear)){
				adapter.setAll(state,[])
			}
			if(!Boolean(action.meta.arg.loadInBackground)){
				state.status = 'pending'
			}

		},

		[fetchByTag.rejected]: (state, action) => {
			state.status = 'failed'
			console.log("rejected")
		},

		[readTags.fulfilled]: (state, action) => {
			const { success, data } = action.payload
			if (success == 1) state.tags = data;
		},

		[createTag.fulfilled]: (state, action) => {
			const { success, data } = action.payload
			if (success == 1) state.tags.push(data);
		},
		
		[createRecipe.fulfilled]: (state, action) => {
			state.status = 'idle'
			adapter.upsertOne(state,action.payload.data)
		},

		[editRecipe.pending] : (state,action) => {
			if(action.meta.arg.hasOwnProperty('use_custom_macros')){
				state.entities[action.meta.arg.recipe_id].use_custom_macros = action.meta.arg.use_custom_macros
			}
		},

		[editRecipe.fulfilled]: (state, action) => {
			state.status = 'idle'
			const update = action.payload.data.recipe
			//prevent custom macro edits from updating state
			delete update.use_custom_macros
			adapter.upsertOne(state,action.payload.data.recipe)
		},

		[deleteDefaultRecipe.pending]: (state, action) => {

		},

		[deleteDefaultRecipe.fulfilled]: (state, action) => {
			state.status = 'idle'
			let list = (current(state.recipesList))
			list = list.filter(recipe => recipe.id !== action.meta.arg.recipe_id)
			state.recipesList = list
		},
		
		[addRecipeMacros.fulfilled]: (state, action) => {
			state.status = 'idle'
			const {id,macros} = action.payload.data.recipe
			state.entities[id].macros = macros
		},

		[removeMacro.pending] : (state,action) => {
			const {recipe_id,id} = action.meta.arg
			const recipe = state.entities[recipe_id]
			recipe.macros = recipe.macros.filter(m=>m.id!==id)
		},

		[removeMacro.fulfilled]: (state, action) => {
			state.status = 'idle'

		},

		[addFoodToRecipe.pending] : (state,action)=>{
			const {recipe_id,food_db_name} = action.meta.arg
			state.entities[recipe_id].foods.push({
				type:'placeholder',
				food_db_name
			})
		},

		[addFoodToRecipe.fulfilled] : (state,action)=>{
			const {recipe_food} =  action.payload.data
			const {recipe_id} = recipe_food
			const recipe = state.entities[recipe_id]
			recipe.foods = recipe.foods.map((item,i)=>{
				if(item.type==='placeholder'&&i+1===recipe_food.recipe_position){
					return recipe_food
				}else {
					return item
				}
			})
		},

		[removeRecipeFood.pending] : (state,action)=>{
			const {id,recipeId} = action.meta.arg
			const recipe = state.entities[recipeId]
			recipe.foods = recipe.foods.filter(
				f => f.id !== id 
			)
		},

		[reorderRecipeFoods.pending] : (state,action)=>{
			const {recipeId,update} = action.meta.arg
			const recipe = state.entities[recipeId]
			recipe.foods = update
		},

		[swapFood.pending] : (state,action)=>{
			const {recipe_id,food,id,update} = action.meta.arg
			const recipe = state.entities[recipe_id]
			recipe.foods = recipe.foods.map(recipeFood=>{
				if (recipeFood.id===id) {
					recipeFood.food = food
					recipeFood.food_db_id = food.food_db_id
					recipeFood.food_db_portion_id = update.food_db_portion_id
				}
				return recipeFood
			})
		},

		[addTagToRecipe.pending] : (state,action)=>{
			const {recipe_id,name,tag_id} = action.meta.arg
			const recipe = state.entities[recipe_id]
			recipe.tags.push({name,id:tag_id})
		},

		[deleteTag.pending] : (state,action)=>{
			const {recipe_id,tag_id} = action.meta.arg
			const recipe = state.entities[recipe_id]
			recipe.tags = recipe.tags.filter(t=>t.id!==tag_id)
		},

		[removeRecipe.pending] : (state,action)=>{
			const {recipe_id} = action.meta.arg
			adapter.removeOne(state,recipe_id)
		},

		[editRecipeFood.pending] : (state,action)=>{
			const {recipeId,id,update} = action.meta.arg
			const recipe = state.entities[recipeId]
			recipe.foods = recipe.foods.map(recipeFood=>{
				if(recipeFood.id === id){
					return {
						...recipeFood,
						...update
					}
				}
				return recipeFood
			})
		},
		[editRecipeMacro.pending] : (state,action)=>{
			const {recipeId,id,update} = action.meta.arg
			const recipe = state.entities[recipeId]
			recipe.macros = recipe.macros.map(macro=>{
				if(macro.id === id){
					return {
						...macro,
						...update
					}
				}
				return macro
			})
		}
	}
})

export const {
	selectAll: selectAllRecipes,
	selectById: selectRecipeById,
	selectIds: selectRecipeIds
} = adapter.getSelectors( state => state.recipes )

export const {setCopy,updateViewer} = recipesSlice.actions


export default recipesSlice.reducer